import React, { useState, useEffect } from 'react';
import { Shield, Search, Plus, Filter, CheckCircle, XCircle, Clock, AlertTriangle, ThumbsUp, ThumbsDown, ChevronDown, ChevronUp } from 'lucide-react';
import { aiReviewService } from '../../services/aiReviewService';


export default function AIReviewProtocols() {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [actionTypes, setActionTypes] = useState([]);
  const [reviewStatuses, setReviewStatuses] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedProtocolId, setExpandedProtocolId] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [protocolsData, actionTypesData, statusesData] = await Promise.all([
        aiReviewService?.getAll(selectedStatus === 'all' ? null : selectedStatus),
        aiReviewService?.getActionTypes(),
        aiReviewService?.getReviewStatuses()
      ]);

      setProtocols(protocolsData);
      setActionTypes(actionTypesData);
      setReviewStatuses(statusesData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (protocolId) => {
    try {
      const details = await aiReviewService?.getById(protocolId);
      setSelectedProtocol(details);
      setExpandedProtocolId(protocolId);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleApprove = async (protocolId) => {
    try {
      await aiReviewService?.updateStatus(protocolId, 'approved', 'Approved after review');
      loadData();
      setExpandedProtocolId(null);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleReject = async (protocolId) => {
    const notes = prompt('Enter rejection reason:');
    if (!notes) return;

    try {
      await aiReviewService?.updateStatus(protocolId, 'rejected', notes);
      loadData();
      setExpandedProtocolId(null);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleCheckpointUpdate = async (checkpointId, passed) => {
    try {
      await aiReviewService?.updateCheckpoint(checkpointId, passed, {
        checked_by: 'admin',
        checked_at: new Date()?.toISOString()
      });
      // Reload the selected protocol to show updated checkpoint
      if (selectedProtocol) {
        handleViewDetails(selectedProtocol?.id);
      }
    } catch (err) {
      setError(err?.message);
    }
  };

  const filteredProtocols = protocols?.filter(protocol =>
    protocol?.actionDescription?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    protocol?.actionType?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'approved': 'bg-green-100 text-green-800 border-green-200',
      'rejected': 'bg-red-100 text-red-800 border-red-200',
      'requires_modification': 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': <Clock className="w-4 h-4" />,
      'approved': <CheckCircle className="w-4 h-4" />,
      'rejected': <XCircle className="w-4 h-4" />,
      'requires_modification': <AlertTriangle className="w-4 h-4" />
    };
    return icons?.[status] || <Clock className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">AI Review Protocols</h1>
                <p className="text-sm text-gray-500 mt-1">Systematic AI Action Validation</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Protocol
            </button>
          </div>

          {/* Search and Filter Bar */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search protocols..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Statuses</option>
                {reviewStatuses?.map(status => (
                  <option key={status?.value} value={status?.value}>{status?.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Protocol Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {reviewStatuses?.map(status => {
            const count = protocols?.filter(p => p?.status === status?.value)?.length;
            return (
              <div key={status?.value} className={`rounded-lg border p-4 ${getStatusColor(status?.value)}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{status?.label}</p>
                    <p className="text-2xl font-bold mt-1">{count}</p>
                  </div>
                  {getStatusIcon(status?.value)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Protocols Table */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredProtocols?.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No protocols found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requested By
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProtocols?.map(protocol => (
                    <React.Fragment key={protocol?.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                            {actionTypes?.find(t => t?.value === protocol?.actionType)?.label || protocol?.actionType}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-md truncate">
                            {protocol?.actionDescription}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(protocol?.status)}`}>
                            {getStatusIcon(protocol?.status)}
                            <span>{reviewStatuses?.find(s => s?.value === protocol?.status)?.label || protocol?.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{protocol?.priority}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {protocol?.requestedByProfile?.email || 'Unknown'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewDetails(protocol?.id)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              {expandedProtocolId === protocol?.id ? (
                                <ChevronUp className="w-5 h-5" />
                              ) : (
                                <ChevronDown className="w-5 h-5" />
                              )}
                            </button>
                            {protocol?.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApprove(protocol?.id)}
                                  className="text-green-600 hover:text-green-900"
                                >
                                  <ThumbsUp className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleReject(protocol?.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <ThumbsDown className="w-5 h-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedProtocolId === protocol?.id && selectedProtocol && (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 bg-gray-50">
                            <ProtocolDetails
                              protocol={selectedProtocol}
                              onCheckpointUpdate={handleCheckpointUpdate}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {/* Add Protocol Modal */}
      {showAddModal && (
        <ProtocolModal
          actionTypes={actionTypes}
          onSave={async (data) => {
            try {
              await aiReviewService?.create(data);
              setShowAddModal(false);
              loadData();
            } catch (err) {
              setError(err?.message);
            }
          }}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}

function ProtocolDetails({ protocol, onCheckpointUpdate }) {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-2">Full Description</h4>
        <p className="text-sm text-gray-700">{protocol?.actionDescription}</p>
      </div>
      {protocol?.approvalNotes && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Review Notes</h4>
          <p className="text-sm text-gray-700">{protocol?.approvalNotes}</p>
        </div>
      )}
      {protocol?.validationRules && protocol?.validationRules?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Validation Rules</h4>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            {protocol?.validationRules?.map((rule, idx) => (
              <li key={idx}>{rule}</li>
            ))}
          </ul>
        </div>
      )}
      {protocol?.checkpoints && protocol?.checkpoints?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Validation Checkpoints</h4>
          <div className="space-y-2">
            {protocol?.checkpoints?.map(checkpoint => (
              <div
                key={checkpoint?.id}
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">{checkpoint?.checkpointName}</span>
                    {checkpoint?.required && (
                      <span className="text-xs text-red-600">(Required)</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{checkpoint?.checkpointType}</span>
                </div>
                <div className="flex items-center space-x-2">
                  {checkpoint?.passed === true ? (
                    <span className="text-green-600 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-1" />
                      Passed
                    </span>
                  ) : checkpoint?.passed === false ? (
                    <span className="text-red-600 flex items-center">
                      <XCircle className="w-5 h-5 mr-1" />
                      Failed
                    </span>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onCheckpointUpdate(checkpoint?.id, true)}
                        className="px-3 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200"
                      >
                        Pass
                      </button>
                      <button
                        onClick={() => onCheckpointUpdate(checkpoint?.id, false)}
                        className="px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200"
                      >
                        Fail
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProtocolModal({ actionTypes, onSave, onClose }) {
  const [formData, setFormData] = useState({
    actionType: 'code_generation',
    actionDescription: '',
    priority: 0,
    checkpoints: []
  });

  const [newCheckpoint, setNewCheckpoint] = useState({
    checkpointName: '',
    checkpointType: 'validation',
    required: true
  });

  const addCheckpoint = () => {
    if (!newCheckpoint?.checkpointName?.trim()) return;
    
    setFormData({
      ...formData,
      checkpoints: [...formData?.checkpoints, { ...newCheckpoint }]
    });
    
    setNewCheckpoint({
      checkpointName: '',
      checkpointType: 'validation',
      required: true
    });
  };

  const removeCheckpoint = (index) => {
    setFormData({
      ...formData,
      checkpoints: formData?.checkpoints?.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Create AI Review Protocol</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
            <select
              value={formData?.actionType}
              onChange={(e) => setFormData({ ...formData, actionType: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              {actionTypes?.map(type => (
                <option key={type?.value} value={type?.value}>{type?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action Description</label>
            <textarea
              required
              rows={4}
              value={formData?.actionDescription}
              onChange={(e) => setFormData({ ...formData, actionDescription: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <input
              type="number"
              value={formData?.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e?.target?.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Validation Checkpoints</label>
            <div className="space-y-2 mb-3">
              {formData?.checkpoints?.map((checkpoint, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">{checkpoint?.checkpointName}</span>
                  <button
                    type="button"
                    onClick={() => removeCheckpoint(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Checkpoint name"
                value={newCheckpoint?.checkpointName}
                onChange={(e) => setNewCheckpoint({ ...newCheckpoint, checkpointName: e?.target?.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="button"
                onClick={addCheckpoint}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Add
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Create Protocol
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}